/**
 * @file DealsFilterBar.tsx
 * @description Фильтры реестра сделок (phase19.31, макет (в)): поиск по
 * названию · «Воронка ▾» · «Только открытые» — одной строкой от 1024.
 *
 * До 1024 в строке только поиск и кнопка «Фильтры · N»: воронка и «только
 * открытые» уходят в нижнюю шторку. Поиск применяется сам через 0,4 с после
 * ввода (и сразу по Enter) — без кнопки «Применить». Воронка — `SelectField`,
 * «только открытые» — пилюля-переключатель, а не нативный флажок.
 */

import { useEffect, useState } from "react";

import { FilterBar } from "../../ui/FilterBar";
import { FilterToggleChips } from "../../ui/FilterChips";
import { FilterSheetButton } from "../../ui/FilterSheetButton";
import { SearchInput } from "../../ui/SearchInput";
import { SelectField, type SelectFieldOption } from "../../ui/SelectField";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { dealsSheetFilterCount, type DealsRegistryQuery } from "../../../lib/crm-deals-query";

/** Задержка поиска: не дёргаем сервер на каждую букву. */
const SEARCH_DELAY_MS = 400;

interface Props {
  query: DealsRegistryQuery;
  onChange: (patch: Partial<DealsRegistryQuery>) => void;
  /** Воронки для выбора; первая опция — «Все воронки». */
  pipelineOptions: SelectFieldOption[];
}

export function DealsFilterBar({ query, onChange, pipelineOptions }: Props) {
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const [search, setSearch] = useState(query.search);

  // Адрес поменялся извне («Сбросить фильтры», «назад») — поле за ним.
  useEffect(() => setSearch(query.search), [query.search]);

  useEffect(() => {
    if (search.trim() === query.search) return;
    const timer = window.setTimeout(() => onChange({ search: search.trim() }), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search, query.search, onChange]);

  const controls = (
    <>
      <SelectField
        ariaLabel="Воронка"
        value={query.pipelineId}
        onChange={(pipelineId) => onChange({ pipelineId })}
        options={pipelineOptions}
        placeholder="Все воронки"
        className={compact ? "w-full" : "w-[220px]"}
      />
      <FilterToggleChips
        aria-label="Отбор сделок"
        items={[
          { key: "open", label: "Только открытые", pressed: query.openOnly, onToggle: () => onChange({ openOnly: !query.openOnly }) },
        ]}
      />
    </>
  );

  return (
    <FilterBar
      search={
        <form
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            onChange({ search: search.trim() });
          }}
        >
          <SearchInput value={search} onChange={setSearch} placeholder="Название сделки" ariaLabel="Поиск по названию сделки" />
        </form>
      }
    >
      {compact ? (
        <FilterSheetButton
          count={dealsSheetFilterCount(query)}
          onReset={() => onChange({ pipelineId: "", openOnly: false })}
        >
          {controls}
        </FilterSheetButton>
      ) : (
        controls
      )}
    </FilterBar>
  );
}
